import { useCallback, useMemo } from 'react'

import type { ApolloClient } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useApolloClient, useMutation } from '@apollo/client/react'
import { useLocation } from 'react-router-dom'

import { getChannelMirrorFlush } from '~publish/components/Composer/store/bridge/channelMirrorFlush'
import { useEditorStateEnabled } from '~publish/components/Composer/store/bridge/useEditorStateEnabled'
import { getComposerStore } from '~publish/components/Composer/store/composerStore'
import { useSelectedTagIds } from '~publish/components/Composer/store/postSlice/hooks'
import { useSaveErrorActions } from '~publish/components/Composer/store/saveErrorsSlice/hooks'
import { supportsInstagramLocationTagging } from '~publish/components/Composer/utils/supportsInstagramLocationTagging'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { useSelectedProfiles } from '~publish/hooks/useSelectedProfiles'
import AppActionCreators from '~publish/legacy/composer/composer/action-creators/AppActionCreators'
import ComposerActionCreators from '~publish/legacy/composer/composer/action-creators/ComposerActionCreators'
import NotificationActionCreators from '~publish/legacy/composer/composer/action-creators/NotificationActionCreators'
import {
  ErrorTypes,
  QueueingTypes,
} from '~publish/legacy/composer/composer/AppConstants'
import { saveYoutubeDefaultsToStorage } from '~publish/legacy/composer/composer/components/youtube/utils'
import type { QueueingType } from './types'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'
import ComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'
import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import AppHooks from '~publish/legacy/composer/composer/utils/LifecycleHooks'
import { getCurrentPageName } from '~publish/legacy/composer/composer/utils/TrackingUtils'
import type { UpdateCallbackOptions } from '~publish/legacy/composer/composer/types'
import { NotificationScopes } from '~publish/legacy/composer/composer/values/Notification'
import { selectNotificationsEnabled } from '~publish/legacy/profile-sidebar/selectors'
import { store } from '~publish/legacy/store'
import { logError } from '~publish/legacy/utils/logError'

import { buildCreatePostInputs } from './buildCreatePostInputs'
import { buildEditPostInput } from './buildEditPostInput'
import { DRAFT_LIST_REFETCH_QUERIES } from './draftListRefetchQueries'
import { selectedBoardServiceId } from './selectedBoardServiceId'
import { toSavedPostRef } from './toSavedPostRef'
import { useCreatePostZustandEnabled } from './useCreatePostZustandEnabled'
import { useEditPostZustandEnabled } from './useEditPostZustandEnabled'
import {
  channelsToClear,
  resolveContentItemErrors,
} from './resolveContentItemErrors'
import type {
  ContentItemError,
  ResolvedContentItemErrors,
} from './resolveContentItemErrors'
import type { ScheduleOption } from '../graphqlEnums'
import type { CreatePostInput } from './graphqlTypes'
import { ApprovalMode } from './types'
import type { MapperContext, ProfileRef, SaveCallbacks } from './types'

export const CreateContentItemDocument = graphql(`
  mutation CreateContentItem($input: CreateContentItemInput!) {
    createContentItem(input: $input) {
      __typename
      ... on CreateContentItemSuccess {
        content {
          id
          # Enough of the created posts to deep-link the success toast; the
          # body is DraftContent for a channel-less item, which this path
          # never creates.
          body {
            ... on PostContent {
              posts {
                id
                channelId
                status
                tags {
                  id
                }
              }
            }
          }
        }
      }
      ... on CreateContentItemFailure {
        message
        errors {
          __typename
          ... on MutationError {
            message
          }
          ... on CreateContentItemVariantInvalidInputError {
            channelId
          }
          ... on CreateContentItemVariantLimitReachedError {
            channelId
          }
          ... on CreateContentItemVariantNotFoundError {
            channelId
          }
        }
      }
    }
  }
`)

// Elevates an existing channel-less draft to channel posts in place, reusing the
// same CreatePostInput variants as create. Its success/failure shapes mirror
// CreateContentItem so toSavedPostRef and resolveContentItemErrors handle both.
const PromoteContentItemDraftToPostsDocument = graphql(`
  mutation PromoteContentItemDraftToPosts(
    $input: PromoteContentItemDraftToPostsInput!
  ) {
    promoteContentItemDraftToPosts(input: $input) {
      __typename
      ... on PromoteContentItemDraftToPostsSuccess {
        contentItem {
          id
          body {
            ... on PostContent {
              posts {
                id
                channelId
                status
                tags {
                  id
                }
              }
            }
          }
        }
      }
      ... on PromoteContentItemDraftToPostsFailure {
        message
        errors {
          __typename
          ... on MutationError {
            message
          }
          ... on PostChannelNotFoundError {
            channelId
          }
          ... on PostInvalidInputError {
            channelId
          }
          ... on PostLimitReachedError {
            channelId
          }
        }
      }
    }
  }
`)

export const ComposerEditPostDocument = graphql(`
  mutation ComposerEditPost($input: EditPostInput!) {
    editPost(input: $input) {
      __typename
      ... on MutationError {
        message
      }
      ... on PostActionSuccess {
        post {
          id
          channelId
          status
          tags {
            id
          }
        }
      }
    }
  }
`)

// Resolved fresh on every request from the accounts that can schedule on the
// channel, so unlike the Redux `hasPushNotifications` flag it cannot go stale
// when a device is registered mid-session (CHAN-12086).
export const ComposerChannelHasActiveMemberDeviceDocument = graphql(`
  query ComposerChannelHasActiveMemberDevice($channelId: ChannelId!) {
    channel(input: { id: $channelId }) {
      id
      hasActiveMemberDevice
    }
  }
`)

export const GENERIC_SAVE_ERROR =
  'We could not save your post. Please try again.'

// Mirrors hasDraftContent's backend rule: a channel-less draft needs text or
// at least one asset. Checked client-side before the mutation runs.
export const EMPTY_DRAFT_ERROR =
  'A draft must include text or at least one asset.'

// A rejected mutation (top-level GraphQL errors with `data: null`) rejects with
// a CombinedGraphQLErrors in Apollo Client v4; its `errors` carry the messages.
// Surface them so a request-level failure shows its real, user-facing text at
// the top of the composer instead of the generic fallback.
export const extractGraphQLMessages = (error: unknown): string[] => {
  if (!CombinedGraphQLErrors.is(error)) {
    return []
  }
  return error.errors
    .map((graphQLError) => graphQLError.message)
    .filter((message) => message.length > 0)
}

// The composer save path is fire-and-forget from most callers' view (they
// await but ignore the result); success and per-channel errors surface via
// side effects. `success` exists for the rare caller that chains a further
// action on the outcome (e.g. a coachmark's channel-reconnect redirect) and
// must not run that action after a failed save. A fresh object per call
// satisfies the `SaveCallbacks` contract without handing out a shared,
// mutable reference.
export const saveResult = (
  success: boolean,
): {
  success: boolean
  successfulResponses: never[]
  unsuccessfulResponses: never[]
} => ({ success, successfulResponses: [], unsuccessfulResponses: [] })

export const createQueueingType = (
  option: ScheduleOption,
  saveToDraft: boolean,
): QueueingType => {
  if (saveToDraft) {
    return option === 'FixedTime'
      ? QueueingTypes.CUSTOM_DRAFT
      : QueueingTypes.ADD_DRAFT
  }
  switch (option) {
    case 'Now':
      return QueueingTypes.NOW
    case 'Prioritize':
      return QueueingTypes.NEXT
    case 'FixedTime':
      return QueueingTypes.CUSTOM
    default:
      return QueueingTypes.QUEUE
  }
}

// Routes each resolved error to its display surface by `kind`. Per-channel:
// `content` → inline error on the channel; `limit` → an upgrade prompt with a
// CTA (not a saving error). Request-level ("general") errors go to the
// top-of-composer banner via `reportGeneralErrors` (a toast would render behind
// the composer dialog). Adding a new surface = add a `kind` branch.
//
// `savedServiceNames` is every channel in this save; channels no longer failing
// get their stale inline error cleared (an all-or-nothing failure returns only
// the still-failing variants, so a fixed channel would otherwise keep its
// error). Still-failing channels are replaced in place via `isUnique` — no
// clear-then-add, so an unchanged error doesn't flicker.
const surfaceErrors = (
  resolved: ResolvedContentItemErrors,
  savedServiceNames: string[],
  reportGeneralErrors: (messages: string[]) => void,
  isOmni = false,
): void => {
  channelsToClear(resolved.channelErrors, savedServiceNames).forEach(
    (serviceName) => {
      AppActionCreators.clearComposerInlineErrors(serviceName)
      ComposerActionCreators.updateDraftHasSavingError(serviceName, false)
    },
  )

  // Omni mode renders one unified editor with no per-channel draft cards, so
  // inline per-channel notices have nowhere to mount. Collect content errors
  // and show them in the general banner instead. Limit errors keep their
  // upgrade-prompt notification, which is not bound to a draft card.
  const generalErrors = [...resolved.generalErrors]

  resolved.channelErrors.forEach(({ serviceName, kind, message }) => {
    if (kind === 'limit') {
      NotificationActionCreators.queueInfo({
        scope: `${NotificationScopes.PROFILE_QUEUE_LIMIT}-${serviceName}`,
        message,
        isUnique: true,
        // Routing on click is handled by the Notification component via
        // `isUpgradePath` (mirrors the legacy queue-limit path).
        cta: { label: 'Upgrade', isUpgradePath: true, action: () => undefined },
      })
      return
    }

    if (isOmni) {
      generalErrors.push(message)
      return
    }

    ComposerActionCreators.updateDraftHasSavingError(serviceName, true)
    NotificationActionCreators.queueError({
      // @ts-expect-error -- ErrorNotification.scope is typed as the bare enum,
      // but inline per-channel errors are keyed by `<UPDATE_SAVING>-INLINE-<service>`
      // at runtime (same suppression the legacy save path uses).
      scope: `${NotificationScopes.UPDATE_SAVING}-${ErrorTypes.INLINE}-${serviceName}`,
      message,
      // Replace the same-scope notice atomically so a persisting error doesn't
      // flicker (no separate clear-then-add).
      isUnique: true,
    })
  })

  // Dedupe: an all-or-nothing failure returns the same message for every
  // failing variant, which would otherwise repeat in the banner.
  const uniqueGeneralErrors = [...new Set(generalErrors)]
  if (uniqueGeneralErrors.length > 0) {
    reportGeneralErrors(uniqueGeneralErrors)
  }
}

// Composer drafts are keyed by service name (`draft.id === draft.service.name`),
// so the action creators take the service name as the draft id. Clear any prior
// inline error before marking saved, mirroring the legacy success path.
const markSaved = (serviceNames: string[]): void => {
  serviceNames.forEach((serviceName) => {
    AppActionCreators.clearComposerInlineErrors(serviceName)
    ComposerActionCreators.updateDraftHasSavingError(serviceName, false)
    ComposerActionCreators.updateDraftIsSaved(serviceName)
  })
}

// The active thread post's live editor content is only committed into
// `draft.thread` when the composer switches/collapses; non-expanded threaded
// drafts were committed when they lost focus. So — mirroring the legacy save —
// flush just the currently-expanded threaded draft before reading drafts.
const flushExpandedThread = (drafts: Draft[]): void => {
  const expandedComposerId = AppStore.getExpandedComposerId()
  if (!expandedComposerId) return

  const isExpandedThreaded = drafts.some(
    (draft) =>
      draft.id === expandedComposerId &&
      draft.hasThread() &&
      draft.service.supportsThread(draft.updateType),
  )
  if (isExpandedThreaded) {
    ComposerActionCreators.switchActiveThreadEditor(expandedComposerId, 0)
  }
}

// Asks the server whether anyone who can schedule on this channel has a device.
//
// Fails open: a reminder post that slips through is visible in the queue, while
// a blocked save leaves the user with no way forward at all — which is the bug
// this check exists to end (CHAN-12086).
const hasLiveMemberDevice = async (
  client: ApolloClient,
  channelId: string,
): Promise<boolean> => {
  try {
    const { data } = await client.query({
      query: ComposerChannelHasActiveMemberDeviceDocument,
      variables: { channelId },
      // The whole point is to bypass anything cached from page load.
      fetchPolicy: 'network-only',
    })
    // A resolved query with no data is a failure the catch never sees, so it
    // falls open the same way.
    return data?.channel.hasActiveMemberDevice ?? true
  } catch (error) {
    logError(error, {
      metaData: { channelId, check: 'hasActiveMemberDevice' },
    })
    return true
  }
}

// A reminder post with no device connected never leaves the queue (CHAN-11738).
// Checked per channel — `selectNotificationsEnabled` is per-profile — so one
// connected channel can't mask a different, disconnected reminder channel.
//
// The Redux flag is only a fast path. It is hydrated once from the legacy
// profile list and nothing invalidates it when a device is registered out of
// band in the mobile app, so a cached `false` has to be re-checked against the
// server or the setup prompt reappears forever (CHAN-12086). A cached `true`
// needs no round trip.
const findReminderChannelWithoutDevice = async (
  drafts: Draft[],
  profiles: ProfileRef[],
  client: ApolloClient,
): Promise<ProfileRef | undefined> => {
  const state = store.getState()
  const reminderProfiles = profiles.filter((profile) => {
    const draft = drafts.find(
      (candidate) => candidate.service.name === profile.serviceName,
    )
    return (
      draft?.isReminder === true &&
      !selectNotificationsEnabled(state, [profile.id])
    )
  })

  if (reminderProfiles.length === 0) {
    return undefined
  }

  // There is no multi-id channel query, so fan out — in practice this is one
  // channel, because a save only reaches here when its draft is a reminder.
  const liveResults = await Promise.all(
    reminderProfiles.map((profile) => hasLiveMemberDevice(client, profile.id)),
  )

  return reminderProfiles.find((_profile, index) => !liveResults[index])
}

/**
 * GraphQL save path (CORE-6125). Fans the enabled drafts out into per-channel
 * variants and creates them atomically via `createContentItem`, or edits a
 * single post via `editPost`.
 *
 * Approval is expressed in the same request as the content: create carries
 * `needsApproval`, edit carries `approvalChange`. Chaining a separate
 * `requestPostApproval` call would not work — the backend gates the approver
 * notification on the approval state actually changing, so a second call would
 * find the flag already set and notify nobody.
 */
export const useGraphCallbacks = (
  option: ScheduleOption = 'Queue',
  scheduledAt: number | null = null,
  initialScheduledAt: number | null = null,
): SaveCallbacks => {
  const organizationId = useOrganizationId()
  const { selectedProfiles } = useSelectedProfiles()
  const selectedTagIds = useSelectedTagIds()
  const [createContentItem] = useMutation(CreateContentItemDocument)
  const [promoteContentItemDraft] = useMutation(
    PromoteContentItemDraftToPostsDocument,
    {
      // The promoted item flips draftContent→postContent, so it must leave the
      // channel-less surfaces (DRAFT_LIST_REFETCH_QUERIES) AND its new
      // channel-specific draft posts must appear in the post surfaces. Pusher
      // alone surfaces them incompletely (only the last variant lands before a
      // reload), so refetch the post list and calendar too.
      refetchQueries: [
        ...DRAFT_LIST_REFETCH_QUERIES,
        'GetPostList',
        'GetCalendarAndPostList',
      ],
    },
  )
  const [editPost] = useMutation(ComposerEditPostDocument)
  const client = useApolloClient()
  const { pathname } = useLocation()
  const { setGeneralSaveErrors, clearGeneralSaveErrors } = useSaveErrorActions()
  const openGlobalModal = useOpenGlobalModal()
  const createPostZustandEnabled = useCreatePostZustandEnabled()
  const editPostZustandEnabled = useEditPostZustandEnabled()
  const editorStateEnabled = useEditorStateEnabled()

  const profiles = useMemo<ProfileRef[]>(
    () =>
      selectedProfiles.map((profile) => ({
        id: profile.id,
        serviceName: profile.serviceName,
        selectedBoardServiceId: selectedBoardServiceId(profile),
        supportsLocationTagging: supportsInstagramLocationTagging(profile),
      })),
    [selectedProfiles],
  )

  // Per-profile fields (`channelId`, `selectedBoardServiceId`,
  // `supportsLocationTagging`) are injected at the call sites; `operation`
  // names which save path (create/edit) builds the context.
  const baseContext = useCallback(
    (
      operation: MapperContext['operation'],
      saveToDraft: boolean | undefined,
      requestApproval?: boolean,
    ): Omit<
      MapperContext,
      'channelId' | 'selectedBoardServiceId' | 'supportsLocationTagging'
    > => ({
      operation,
      selectedTagIds,
      scheduleOption: option,
      customScheduleTime: scheduledAt,
      // Edit-only: lets the edit mapper tell a real schedule change from the
      // selector's default `FixedTime` seed (see `resolveEditScheduling`).
      initialScheduledAt,
      // Origin surface for tracking (legacy `created_source`), derived from the
      // current route like the legacy save path.
      source: getCurrentPageName(pathname),
      saveToDraft,
      requestApproval,
    }),
    [selectedTagIds, option, scheduledAt, initialScheduledAt, pathname],
  )

  const enabledDrafts = useCallback(
    () =>
      ComposerStore.getEnabledDrafts().filter(
        (draft) =>
          // Skip the omni draft and any locked (already-saved) draft, matching
          // the legacy save path so a reused composer never re-sends them.
          draft.service.name !== 'omni' &&
          !ComposerStore.isDraftLocked(draft.id),
      ),
    [],
  )

  const create = useCallback(
    async ({
      saveToDraft,
      requestApproval,
      willCreateAnotherPost = false,
    }: {
      saveToDraft: boolean
      requestApproval?: boolean
      willCreateAnotherPost?: boolean
    }) => {
      // Clear stale banner errors so a retry starts clean.
      clearGeneralSaveErrors()

      if (!organizationId) {
        setGeneralSaveErrors([GENERIC_SAVE_ERROR])
        return saveResult(false)
      }

      // In omni mode the omni draft is the source of truth for content; the
      // per-channel drafts this save reads still hold stale content from
      // channel-enable time. Flush the omni content onto them before reading.
      if (AppStore.getIsOmniboxEnabled()) {
        ComposerActionCreators.applyOmniContentToDrafts()
      }

      // Commit the expanded draft's active thread post, then read the drafts.
      flushExpandedThread(enabledDrafts())
      const drafts = enabledDrafts()

      if (!saveToDraft) {
        const reminderChannel = await findReminderChannelWithoutDevice(
          drafts,
          profiles,
          client,
        )
        if (reminderChannel) {
          openGlobalModal(MODALS.setupNotifications, { source: 'composer' })
          return saveResult(false)
        }
      }

      // buildCreatePostInputs throws NotImplemented for a channel whose service
      // has no mapper yet. Surface the generic banner so the user sees a save
      // error rather than an unhandled rejection (the footer has no catch of its
      // own).
      let posts: CreatePostInput[]
      try {
        // Settle the debounced channel mirror before reading channelVariants
        // — the payload-drift check always runs when a variant exists, so a
        // stale read would report drift that isn't real, and once the flag
        // is on a stale variant could also ship.
        getChannelMirrorFlush()?.()

        posts = buildCreatePostInputs(
          drafts,
          profiles,
          baseContext('create', saveToDraft, requestApproval),
          getComposerStore().getState().channelVariants,
          createPostZustandEnabled,
          editorStateEnabled,
        )
      } catch (error) {
        const messages = extractGraphQLMessages(error)
        setGeneralSaveErrors(
          messages.length > 0 ? messages : [GENERIC_SAVE_ERROR],
        )
        return saveResult(false)
      }

      // createContentItem requires at least one variant. Selected profiles and
      // enabled drafts are separate sources that can diverge, so a mismatch
      // could otherwise send an empty list the server would reject.
      if (posts.length === 0) {
        setGeneralSaveErrors([GENERIC_SAVE_ERROR])
        return saveResult(false)
      }

      const reportSuccess = (
        content: Parameters<typeof toSavedPostRef>[0],
      ): void => {
        markSaved(drafts.map((draft) => draft.service.name))
        saveYoutubeDefaultsToStorage(drafts)
        AppHooks.handleSavedDrafts({
          fromIdea: false,
          entityName: 'post',
          channelIds: posts.map((post: CreatePostInput) => post.channelId),
          draftMode: saveToDraft,
          queueingType: createQueueingType(option, saveToDraft),
          isPlural: posts.length > 1,
          willCreateAnotherPost,
          // Drives the success-confirmation copy ("the approval request is on
          // its way") — see GetComposerConfirmationMessage.
          ...(requestApproval === true
            ? { approvalMode: ApprovalMode.REQUEST_APPROVAL }
            : {}),
          savedPost: toSavedPostRef(content),
        })
      }

      const reportFailure = (errors: readonly ContentItemError[]): void => {
        surfaceErrors(
          resolveContentItemErrors(errors, profiles),
          drafts.map((draft) => draft.service.name),
          setGeneralSaveErrors,
          AppStore.getIsOmniboxEnabled() === true,
        )
      }

      try {
        // A composer opened from an existing channel-less draft carries its id
        // in omni. Saving/scheduling with channels selected must promote that
        // draft in place — create the posts AND retire the ContentItem — rather
        // than create a fresh item, which would orphan the original draft. Read
        // inside try: getComposerStore throws if the store is uninitialised.
        const contentItemDraftId = getComposerStore().getState().omni.draftId

        if (contentItemDraftId) {
          const { data } = await promoteContentItemDraft({
            // Tags ride each post variant (via buildCreatePostInputs) and are
            // also set on the promoted ContentItem parent.
            variables: {
              input: { id: contentItemDraftId, posts, tagIds: selectedTagIds },
            },
          })
          const payload = data?.promoteContentItemDraftToPosts

          if (payload?.__typename === 'PromoteContentItemDraftToPostsSuccess') {
            // The draft is consumed; drop the seed so a reused composer (Create
            // Another) creates fresh instead of re-promoting a spent id.
            getComposerStore().getState().setOmniDraftId(null)
            reportSuccess(payload.contentItem)
            return saveResult(true)
          }
          if (payload?.__typename === 'PromoteContentItemDraftToPostsFailure') {
            // Keep the seed on failure so a fixed error retries the promote. A
            // double-submit (draft already consumed) re-fails with a
            // ContentItemStateError rather than duplicating, and refetchQueries
            // ran regardless, so the draft surfaces already reflect reality.
            reportFailure(payload.errors)
            return saveResult(false)
          }

          setGeneralSaveErrors([GENERIC_SAVE_ERROR])
          return saveResult(false)
        }

        const { data } = await createContentItem({
          // Tags ride each post variant (via buildCreatePostInputs) and are also
          // set on the ContentItem parent so the group carries them too. Title
          // is typed locally pre-save (no draftId yet to update directly) and
          // rides along here the same way.
          variables: {
            input: {
              organizationId,
              posts,
              tagIds: selectedTagIds,
              title: getComposerStore().getState().omni.title,
            },
          },
        })
        const payload = data?.createContentItem

        if (payload?.__typename === 'CreateContentItemSuccess') {
          reportSuccess(payload.content)
          return saveResult(true)
        }

        if (payload?.__typename === 'CreateContentItemFailure') {
          reportFailure(payload.errors)
          return saveResult(false)
        }

        setGeneralSaveErrors([GENERIC_SAVE_ERROR])
        return saveResult(false)
      } catch (error) {
        const messages = extractGraphQLMessages(error)
        setGeneralSaveErrors(
          messages.length > 0 ? messages : [GENERIC_SAVE_ERROR],
        )
        return saveResult(false)
      }
    },
    [
      organizationId,
      enabledDrafts,
      profiles,
      baseContext,
      createContentItem,
      promoteContentItemDraft,
      selectedTagIds,
      option,
      openGlobalModal,
      client,
      setGeneralSaveErrors,
      clearGeneralSaveErrors,
      createPostZustandEnabled,
      editorStateEnabled,
    ],
  )

  // Shared by `updatePost` and `updatePostWithApproval`: one editPost call that
  // saves the content and, when an approval intent is present, changes the
  // approval state in the same request — matching the legacy path, which also
  // did both in one call.
  const edit = useCallback(
    async (options?: UpdateCallbackOptions & { requestApproval?: boolean }) => {
      // Clear stale banner errors so a retry starts clean.
      clearGeneralSaveErrors()

      const { updateId } = AppStore.getMetaData()
      // Commit the expanded draft's active thread post before reading.
      flushExpandedThread(enabledDrafts())
      const [draft] = enabledDrafts()
      const profile = profiles.find(
        (candidate) => candidate.serviceName === draft?.service.name,
      )

      if (!updateId || !draft || !profile) {
        setGeneralSaveErrors([GENERIC_SAVE_ERROR])
        return saveResult(false)
      }

      // Tri-state so the server can tell an explicit status change from "leave
      // it": 'draft' (Move to Drafts) → save as draft; 'post' (Schedule Post /
      // Publish Now) → force out of draft; no newStatus → omit, keep current
      // status. `saveToDraft: false` must be sent explicitly — the server keeps
      // a draft a draft otherwise.
      const saveToDraft =
        options?.newStatus === undefined
          ? undefined
          : options.newStatus === 'draft'

      // Only the draft→scheduled transition newly queues the post. Checked
      // before the await so a content-only edit or a Move to Drafts never pays
      // for the device round trip.
      if (saveToDraft === false) {
        const reminderChannel = await findReminderChannelWithoutDevice(
          [draft],
          [profile],
          client,
        )
        if (reminderChannel) {
          openGlobalModal(MODALS.setupNotifications, { source: 'composer' })
          return saveResult(false)
        }
      }

      try {
        // Inside the try so a channel whose service has no mapper yet
        // (`NotImplemented`) surfaces as the generic banner rather than an
        // unhandled rejection — the footer has no catch of its own.
        const editContext: MapperContext = {
          ...baseContext('edit', saveToDraft, options?.requestApproval),
          channelId: profile.id,
          selectedBoardServiceId: profile.selectedBoardServiceId,
          supportsLocationTagging: profile.supportsLocationTagging,
        }

        // Settle the mirror before reading channelVariants — the
        // payload-drift check always runs when a variant exists, so a stale
        // read would report drift that isn't real, and once the flag is on a
        // stale variant sends explicit clears that delete data, not just
        // omit fields.
        getChannelMirrorFlush()?.()

        const input = buildEditPostInput(
          draft,
          editContext,
          updateId,
          getComposerStore().getState().channelVariants[profile.id],
          editPostZustandEnabled,
          editorStateEnabled,
        )
        const { data } = await editPost({ variables: { input } })
        const payload = data?.editPost

        if (payload?.__typename === 'PostActionSuccess') {
          markSaved([draft.service.name])
          saveYoutubeDefaultsToStorage([draft])
          AppHooks.handleSavedDrafts({
            fromIdea: false,
            entityName: 'post',
            channelIds: [profile.id],
            draftMode: saveToDraft === true,
            queueingType: QueueingTypes.SAVE,
            isPlural: false,
            statusChange: options?.newStatus,
            // Drives the success-confirmation copy ("the approval request is on
            // its way" / "was reverted") — see GetComposerConfirmationMessage.
            ...(options?.requestApproval !== undefined
              ? {
                  approvalMode: options.requestApproval
                    ? ApprovalMode.REQUEST_APPROVAL
                    : ApprovalMode.REVERT_APPROVAL,
                }
              : {}),
            savedPost: {
              id: payload.post.id,
              channelId: payload.post.channelId,
              status: payload.post.status,
              tagIds: payload.post.tags.map((tag) => tag.id),
            },
          })
          return saveResult(true)
        }

        // editPost errors aren't typed per-kind (native → UnexpectedError, proxy
        // → RestProxyError), so surface them as an inline content error.
        surfaceErrors(
          {
            channelErrors: [
              {
                channelId: profile.id,
                serviceName: draft.service.name,
                kind: 'content',
                message: payload?.message ?? GENERIC_SAVE_ERROR,
              },
            ],
            generalErrors: [],
          },
          [draft.service.name],
          setGeneralSaveErrors,
        )
        return saveResult(false)
      } catch (error) {
        const messages = extractGraphQLMessages(error)
        setGeneralSaveErrors(
          messages.length > 0 ? messages : [GENERIC_SAVE_ERROR],
        )
        return saveResult(false)
      }
    },
    [
      enabledDrafts,
      profiles,
      baseContext,
      editPost,
      openGlobalModal,
      client,
      setGeneralSaveErrors,
      clearGeneralSaveErrors,
      editPostZustandEnabled,
      editorStateEnabled,
    ],
  )

  return useMemo(
    () => ({
      schedulePost: (options): ReturnType<SaveCallbacks['schedulePost']> =>
        create({
          saveToDraft: false,
          willCreateAnotherPost: options?.willCreateAnotherPost,
        }),
      saveAsDraft: (options): ReturnType<SaveCallbacks['saveAsDraft']> =>
        create({
          saveToDraft: true,
          willCreateAnotherPost: options?.willCreateAnotherPost,
        }),
      // Only rendered for a selection that requires approval, so the draft is
      // always the target — the server rejects `needsApproval` on a scheduled
      // post, and on a direct poster.
      saveAndRequestApproval: (
        options,
      ): ReturnType<SaveCallbacks['saveAndRequestApproval']> =>
        create({
          saveToDraft: true,
          requestApproval: true,
          willCreateAnotherPost: options?.willCreateAnotherPost,
        }),
      updatePost: edit,
      // No status change: the post is a draft and stays one; only the approval
      // state moves.
      updatePostWithApproval: ({
        needsApproval,
      }): ReturnType<SaveCallbacks['updatePostWithApproval']> =>
        edit({ requestApproval: needsApproval }),
    }),
    [create, edit],
  )
}
